import React, { useEffect, useMemo, useRef } from "react";
import { HpbChartData, HpbPoint } from "src/helpers";
import { DASHBOARD_COMPACT_ECHARTS_GRID } from "src/helpers/chart-helpers";
import * as echarts from "echarts";

type HPBChartProps = {
  hpbChart: HpbPoint[] | HpbChartData;
  darkMode: boolean;
  isAnalyzeStock?: boolean;
  isDashboard?: boolean;
  isExpand?: boolean;
  isMonthWise?: boolean;
  toggleChart?: boolean;
  isMarketHPB?: boolean;
  isOrderblock?: boolean;
  selectedMonthNumber?: number;
  selectedSegment?: string;
  onBarClick?: (monthIndex: number) => void;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SEGMENT_COLORS: Record<string, string> = {
  "Large Cap": "#B0DEE7",
  "Mid Cap": "#E35B41",
  "Small Cap": "#dab50f",
  "Micro Cap": "#0078d4",
};

const SEGMENT_DARK_COLORS: Record<string, string> = {
  "Large Cap": "#8CC8D6",
  "Mid Cap": "#C84731",
  "Small Cap": "#B8980C",
  "Micro Cap": "#0060A8",
};

function getPaddedValueAxis(
  values: number[],
  isDashboard: boolean | undefined,
  isAnalyzeStock: boolean | undefined,
): { min: number; max: number; scale: boolean } {
  if (values.length === 0) {
    return { min: 0, max: 10, scale: false };
  }

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const range = Math.max(dataMax - dataMin, 1);
  const padding = isDashboard
    ? Math.max(range * 0.28, 1.2)
    : isAnalyzeStock
      ? Math.max(range * 0.35, 1.2)
      : Math.max(range * 0.15, 1);

  return {
    min: dataMin - padding,
    max: dataMax + padding,
    scale: !isDashboard,
  };
}

const HPBChart = ({
  hpbChart,
  darkMode,
  isAnalyzeStock,
  isOrderblock,
  isExpand,
  isMonthWise,
  toggleChart,
  selectedMonthNumber,
  isDashboard,
  selectedSegment,
  isMarketHPB,
  onBarClick,
}: HPBChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const monthName = selectedMonthNumber && MONTH_NAMES[selectedMonthNumber - 1];

  const data = hpbChart as HpbChartData;
  const monthWiseSource = useMemo(() => {
    let source = toggleChart
      ? data.sebi?.filter((item) => item.segment !== "NA")
      : data.marketcap?.filter((item) => item.segment !== "NA");

    if (selectedSegment != "All") {
      source = source?.filter((item) => item.segment === selectedSegment);
    }

    return source ?? [];
  }, [data, toggleChart, selectedSegment]);

  // 1. Initialize ECharts and ResizeObserver (re-run only when darkMode changes)
  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(
      chartRef.current,
      darkMode ? "dark" : "light",
    );

    let resizeTimer: ReturnType<typeof setTimeout>;
    let width = chartRef.current?.clientWidth;
    let height = chartRef.current?.clientHeight;

    const observer = new ResizeObserver(() => {
      if (!chartRef.current) return;
      const newWidth = chartRef.current.clientWidth;
      const newHeight = chartRef.current.clientHeight;

      if (newWidth === width && newHeight === height) return;

      width = newWidth;
      height = newHeight;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        chartInstance.current?.resize({
          animation: { duration: 300, easing: "cubicOut" },
        });
      }, 100);
    });

    observer.observe(chartRef.current);

    return () => {
      observer.disconnect();
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [darkMode]);

  // 2. Generate and set chart options (re-run when data or visual dependencies change)
  useEffect(() => {
    if (!chartInstance.current) return;

    const option = isMonthWise
      ? (() => {
          const values = monthWiseSource.map((item) =>
            Number(item.average_growth),
          );
          const yAxis = getPaddedValueAxis(values, isDashboard, isAnalyzeStock);

          return {
            backgroundColor: "transparent",
            legend: {
              show: true,
              bottom: 0,
              left: "center",
              data: ["Large Cap", "Mid Cap", "Small Cap", "Micro Cap"],
              itemWidth: 14,
              itemHeight: 7,
            },
            xAxis: {
              type: "category",
              data: [monthName],
              axisLine: { show: false },
              axisTick: { show: false },
            },
            yAxis: {
              type: "value",
              scale: yAxis.scale,
              splitLine: { show: false },
              axisLabel: {
                formatter: (value: number) => `${value.toFixed(1)}%`,
              },
              min: yAxis.min,
              max: yAxis.max,
            },
            grid: {
              top: 30,
              left: 8,
              right: 8,
              bottom: 30,
              containLabel: true,
            },
            series: monthWiseSource.map((item) => ({
              name: item.segment,
              type: "bar",
              barWidth: 35,
              barCategoryGap: "0%",
              barGap: "0%",
              data: [Number(item.average_growth)],
              itemStyle: {
                borderRadius: [3, 3, 3, 3],
                color: SEGMENT_COLORS[item.segment] ?? "#999",
                shadowColor: (params: { value: number }) =>
                  params.value >= 0
                    ? "rgba(135, 164, 170, 0.3)"
                    : "rgba(160,60,40,0.25)",
                shadowBlur: 2,
                shadowOffsetX: 0,
                shadowOffsetY: 1,
              },
              emphasis: {
                itemStyle: {
                  color: SEGMENT_DARK_COLORS[item.segment] ?? "#777",
                },
              },
              animation: true,
              animationDuration: 1000,
              animationEasing: "cubicOut",
              animationDelay: (idx: number) => idx * 80,
              label: {
                show: true,
                fontSize: 11,
                position: "top",
                formatter: (params: { value: string }) => {
                  const num = Number(params.value);
                  const truncated = Math.trunc(num * 100) / 100;
                  return `${truncated.toFixed(1)}%`;
                },
              },
            })),
          };
        })()
      : (() => {
          const points = hpbChart as HpbPoint[];
          const values = points.map((item) => Number(item.average_growth));
          const yAxis = getPaddedValueAxis(values, isDashboard, isAnalyzeStock);
          const axisLabelRotate = isDashboard ? 28 : isExpand ? 35 : 35;
          return {
            backgroundColor: "transparent",
            xAxis: {
              type: "category",
              axisLabel: {
                interval: 0,
                rotate: axisLabelRotate,
                fontSize: isMarketHPB
                  ? 11
                  : isDashboard
                    ? 9
                    : isExpand
                      ? 12
                      : 11,
                margin: isDashboard ? 8 : isMarketHPB ? 12 : 8,
                color: isMarketHPB
                  ? darkMode
                    ? "#cbd5e1"
                    : "#334155"
                  : darkMode
                    ? "#94a3b8"
                    : "#64748b",
                formatter: (value: string, index: number) => {
                  if (selectedMonthNumber === index + 1) {
                    return `{selected|${value}}`;
                  }
                  return value;
                },
                rich: {
                  selected: {
                    color: darkMode ? "#ffffff" : "#0f172a",
                    fontWeight: "bold",
                  },
                },
              },
              data: [
                "Jan",
                "Feb",
                "Mar",
                "April",
                "May",
                "June",
                "July",
                "Aug",
                "Sept",
                "Oct",
                "Nov",
                "Dec",
              ],
              axisLine: { show: false },
              axisTick: { show: false },
            },
            yAxis: {
              type: "value",
              scale: yAxis.scale,
              splitLine: { show: false },
              axisLabel: {
                formatter: (value: number) => `${value.toFixed(1)}%`,
                fontSize: isMarketHPB ? 10 : isDashboard ? 9 : 11,
                color: isMarketHPB
                  ? darkMode
                    ? "#cbd5e1"
                    : "#334155"
                  : darkMode
                    ? "#94a3b8"
                    : "#64748b",
                margin: isMarketHPB ? 12 : 8,
              },
              min: yAxis.min,
              max: yAxis.max,
            },
            grid: isDashboard
              ? { ...DASHBOARD_COMPACT_ECHARTS_GRID }
              : {
                  top: isMarketHPB ? 40 : isExpand ? 24 : 20,
                  left: isMarketHPB ? 10 : 6,
                  right: isMarketHPB ? 32 : 6,
                  bottom: isMarketHPB
                    ? 55
                    : isExpand
                      ? 12
                      : isOrderblock
                        ? 24
                        : 8,
                  containLabel: true,
                },
            series: [
              {
                data: points.map((item, index) => {
                  const value = Number(item.average_growth);
                  const isSelected = selectedMonthNumber === index + 1;
                  return {
                    value,
                    itemStyle: {
                      color: isSelected
                        ? value >= 0
                          ? darkMode
                            ? "#39a1b6"
                            : "#6bcadd"
                          : darkMode
                            ? "#aa4736"
                            : "#aa4736"
                        : value >= 0
                          ? "#B0DEE7"
                          : "#E35B41",
                      borderRadius: [3, 3, 3, 3],
                    },
                  };
                }),
                type: "bar",
                label: {
                  show: true,
                  position: "top",
                  fontSize: isAnalyzeStock
                    ? isExpand
                      ? 14
                      : 9
                    : isExpand
                      ? 14
                      : isDashboard
                        ? 10
                        : 13,
                  color: darkMode ? "#ffffff" : "#222222",
                  formatter: (params: { value: number }) => {
                    const truncated = Math.trunc(params.value * 100) / 100;
                    return `${truncated.toFixed(1)}%`;
                  },
                },
                animation: true,
                animationDuration: 1000,
                animationEasing: "cubicOut",
                animationDelay: (idx: number) => idx * 80,
              },
            ],
          };
        })();

    chartInstance.current.off("click");
    if (onBarClick) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chartInstance.current.on("click", (params: any) => {
        if (params.componentType === "series") {
          onBarClick(params.dataIndex + 1);
        }
      });
    }

    // Use notMerge: false so that updating styles (e.g. on click) doesn't clear the chart and replay entrance animations.
    chartInstance.current.setOption(option, false);
  }, [
    hpbChart,
    darkMode,
    isDashboard,
    isAnalyzeStock,
    isExpand,
    isMonthWise,
    isOrderblock,
    monthWiseSource,
    monthName,
    onBarClick,
    selectedMonthNumber,
    isMarketHPB,
  ]);

  if (isMonthWise && monthWiseSource.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[250px] text-gray-500">
        No data found
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <div
        ref={chartRef}
        id="hpb-chart"
        className={`w-full flex-1 ${isExpand ? "h-full min-h-0" : isAnalyzeStock ? "h-full min-h-0" : isMonthWise ? "min-h-[30vh]" : isOrderblock ? "h-full" : isDashboard ? "min-h-0" : "min-h-[260px]"}`}
      />
    </div>
  );
};

export default React.memo(HPBChart);
